/**
 * Calendar (date-only) values such as date of birth.
 *
 * API date-only fields often arrive as UTC midnight (`T00:00:00.000Z`) or UTC noon
 * (`T12:00:00.000Z`). Formatting those with the local timezone shifts the day
 * west of UTC. Always read the civil Y-M-D instead.
 */

export type CivilDateParts = {
  year: number;
  month: number;
  day: number;
};

export type DatePickerParts = {
  day: string;
  month: string;
  year: string;
};

const YMD_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;
const FALLBACK_DATE_LABEL = "-";

function isUtcDateOnlyInstant(date: Date): boolean {
  if (
    date.getUTCMinutes() !== 0 ||
    date.getUTCSeconds() !== 0 ||
    date.getUTCMilliseconds() !== 0
  ) {
    return false;
  }
  const hours = date.getUTCHours();
  return hours === 0 || hours === 12;
}

export function getCivilDateParts(
  value: Date | string | null | undefined,
): CivilDateParts | null {
  if (value == null || value === "") return null;

  if (typeof value === "string") {
    const match = value.trim().match(YMD_PREFIX);
    if (match) {
      return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
      };
    }
  }

  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return null;

  if (isUtcDateOnlyInstant(date)) {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    };
  }

  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

export function formatCivilDate(
  value: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  },
  locale = "en-US",
): string | null {
  const parts = getCivilDateParts(value);
  if (!parts) return null;
  const atUtcNoon = new Date(
    Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0),
  );
  return atUtcNoon.toLocaleDateString(locale, {
    ...options,
    timeZone: "UTC",
  });
}

export function formatCivilDateOrFallback(
  value: Date | string | null | undefined,
  fallback = FALLBACK_DATE_LABEL,
): string {
  return formatCivilDate(value) ?? fallback;
}

const LEDGER_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
};

const LEDGER_DATE_LONG_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: "short",
  month: "short",
  day: "numeric",
  year: "numeric",
};

/** Short ledger date that keeps date-only API values on the civil day. */
export function formatCivilDateShort(
  value: Date | string | null | undefined,
  fallback = FALLBACK_DATE_LABEL,
): string {
  return formatCivilDate(value, LEDGER_DATE_OPTIONS) ?? fallback;
}

/** Weekday + short date for ledger detail views. */
export function formatCivilDateLong(
  value: Date | string | null | undefined,
  fallback = FALLBACK_DATE_LABEL,
): string {
  return formatCivilDate(value, LEDGER_DATE_LONG_OPTIONS) ?? fallback;
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

export function toDatePickerValue(
  value: Date | string | null | undefined,
): DatePickerParts {
  const parts = getCivilDateParts(value);
  if (!parts) return { day: "", month: "", year: "" };
  return {
    day: String(parts.day),
    month: String(parts.month),
    year: String(parts.year),
  };
}

export function toCivilYmd(
  value: Date | string | null | undefined,
): string | null {
  const parts = getCivilDateParts(value);
  if (!parts) return null;
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

/** Persist a YYYY-MM-DD calendar day as UTC noon so US timezones keep the same date. */
export function utcNoonFromYmd(ymd: string): Date {
  return new Date(`${ymd}T12:00:00.000Z`);
}
